class SatellaiHomeRedesign {
  constructor() {
    this.revealNodes = new WeakSet();
    this.counterNodes = new WeakSet();
    this.reviewNodes = new WeakSet();
    this.steadytrackNodes = new WeakSet();
    this.appNodes = new WeakSet();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeModal = null;
    this.modalTrigger = null;
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.handleModalKeydown = this.handleModalKeydown.bind(this);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('click', this.handleModalClick);
    document.addEventListener('keydown', this.handleModalKeydown);
  }

  /** 初始化页面及主题编辑器动态插入的 section。 */
  init(root = document) {
    this.setupReveal(root);
    this.setupCounters(root);
    this.setupReviewCarousels(root);
    this.setupSteadytrack(root);
    this.setupAppSections(root);
  }

  setupReveal(root) {
    const nodes = root.matches?.('[data-shr-reveal]') ? [root] : root.querySelectorAll('[data-shr-reveal]');
    nodes.forEach((node) => {
      if (this.revealNodes.has(node)) return;
      this.revealNodes.add(node);
      if (this.reduceMotion.matches) {
        node.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        node.classList.add('is-visible');
        observer.disconnect();
      }, { threshold: .12 });
      observer.observe(node);
    });
  }

  setupCounters(root) {
    root.querySelectorAll('[data-count]').forEach((node) => {
      if (this.counterNodes.has(node)) return;
      this.counterNodes.add(node);
      const target = Number(node.dataset.count || 0);
      const suffix = node.dataset.suffix || '';
      const render = (value) => { node.textContent = Math.round(value).toLocaleString('en-US') + suffix; };
      if (this.reduceMotion.matches) {
        render(target);
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const startedAt = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - startedAt) / 1200, 1);
          render(target * (1 - Math.pow(1 - progress, 4)));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: .35 });
      observer.observe(node);
    });
  }

  /** 评价轮播按单张卡片滑动，并随滚动和尺寸变化同步按钮状态。 */
  setupReviewCarousels(root) {
    const sections = root.matches?.('.shr-social') ? [root] : root.querySelectorAll('.shr-social');
    sections.forEach((section) => {
      if (this.reviewNodes.has(section)) return;
      const track = section.querySelector('[data-shr-horizontal-scroll]');
      const previous = section.querySelector('[data-shr-review-prev]');
      const next = section.querySelector('[data-shr-review-next]');
      if (!track || !previous || !next) return;
      this.reviewNodes.add(section);

      const updateButtons = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll - 2;
      };
      const move = (direction) => {
        const card = track.querySelector('.shr-review');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        track.scrollBy({
          left: direction * (card.getBoundingClientRect().width + gap),
          behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
        });
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', updateButtons, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateButtons).observe(track);
      requestAnimationFrame(updateButtons);
    });
  }

  /** SteadyTrack 区块：点击标题切换展开项；桌面端滚动经过时自动展开最接近视口中心的条目。 */
  setupSteadytrack(root) {
    const sections = root.matches?.('.shr-steadytrack') ? [root] : root.querySelectorAll('.shr-steadytrack');
    sections.forEach((section) => {
      if (this.steadytrackNodes.has(section)) return;
      const items = Array.from(section.querySelectorAll('[data-shr-steadytrack-item]'));
      if (!items.length) return;
      this.steadytrackNodes.add(section);

      const setActive = (index) => {
        items.forEach((item, itemIndex) => {
          const active = itemIndex === index;
          item.classList.toggle('is-active', active);
          const trigger = item.querySelector('[data-shr-steadytrack-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', String(active));
        });
      };
      items.forEach((item, index) => {
        item.querySelector('[data-shr-steadytrack-trigger]')?.addEventListener('click', () => setActive(index));
      });

      const desktop = window.matchMedia('(min-width: 811px)');
      let ticking = false;
      const update = () => {
        ticking = false;
        if (!desktop.matches) return;
        const centerY = window.innerHeight / 2;
        const sectionRect = section.getBoundingClientRect();
        if (sectionRect.top > centerY || sectionRect.bottom < centerY) return;
        let bestIndex = -1;
        let bestDistance = Infinity;
        items.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height / 2 - centerY);
          if (distance < bestDistance) { bestDistance = distance; bestIndex = index; }
        });
        if (bestIndex >= 0) setActive(bestIndex);
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      document.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      update();
    });
  }

  /** App 区块：手风琴单开切换并联动左右媒体，逻辑与 Satellai Accordion 保持一致。 */
  setupAppSections(root) {
    const sections = root.matches?.('[data-shr-app]') ? [root] : root.querySelectorAll('[data-shr-app]');
    sections.forEach((section) => {
      if (this.appNodes.has(section)) return;
      const items = Array.from(section.querySelectorAll('[data-app-item]'));
      if (!items.length) return;
      this.appNodes.add(section);

      const loadVideo = (video) => {
        if (!video || video.dataset.loaded === 'true') return;
        if (video.dataset.poster && !video.getAttribute('poster')) video.setAttribute('poster', video.dataset.poster);
        video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
        video.dataset.loaded = 'true';
        video.load();
      };

      // 桌面端播放右侧媒体位的视频；移动端图片/视频内嵌在展开的手风琴子项里，直接在子项内查找。其余暂停。
      const playActive = () => {
        const isDesktop = window.matchMedia('(min-width: 811px)').matches;
        const scope = isDesktop
          ? section.querySelector('.shr-app__visual--desktop .shr-app__media-item.is-active')
          : section.querySelector('.shr-app__item.is-active .shr-app__item-media');
        section.querySelectorAll('video[data-app-video]').forEach((video) => {
          if (!scope || !scope.contains(video)) video.pause();
        });
        const activeVideo = scope ? scope.querySelector('video[data-app-video]') : null;
        if (!activeVideo) return;
        loadVideo(activeVideo);
        activeVideo.play().catch(() => {});
      };

      // 点击已展开的项要收起（再点第二次收缩），而不是保持常开；
      // 收起时媒体位保留最后一次展开的图片/视频，不清空，避免出现空白。
      const setActive = (index, track = true) => {
        const target = items[index];
        if (!target) return;
        const wasActive = target.classList.contains('is-active');
        const nextIndex = wasActive ? -1 : index;

        if (track) {
          items.forEach((item) => {
            if (item.classList.contains('is-active')) this.trackAppDetails(item, 'fold');
          });
          if (nextIndex !== -1) this.trackAppDetails(target, 'expand');
        }

        items.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === nextIndex);
          const header = item.querySelector('[data-app-accordion-trigger]');
          if (header) header.setAttribute('aria-expanded', String(itemIndex === nextIndex));
        });

        if (nextIndex !== -1) {
          section.querySelectorAll('[data-app-media-desktop]').forEach((node) => {
            node.classList.toggle('is-active', node.dataset.appMediaDesktop === String(nextIndex));
          });
        }

        if (section.dataset.appInView === 'true') playActive();
      };

      items.forEach((item, index) => {
        item.querySelector('[data-app-accordion-trigger]')?.addEventListener('click', () => setActive(index));
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          section.dataset.appInView = entry.isIntersecting ? 'true' : 'false';
          if (entry.isIntersecting) {
            playActive();
          } else {
            section.querySelectorAll('video[data-app-video]').forEach((video) => video.pause());
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.1 });
      observer.observe(section);
    });
  }

  /** 与 Satellai Accordion 相同的手风琴展开/收起埋点事件。 */
  trackAppDetails(item, status) {
    document.dispatchEvent(new CustomEvent('gtm:details-clicked', {
      detail: {
        title: item.dataset.gtmDetailsTitle || item.querySelector('.shr-app__item-title')?.textContent?.trim() || '',
        moduleName: item.dataset.gtmModuleName || 'the satellai app',
        status
      }
    }));
  }

  /** 事件委托：处理弹窗的打开与关闭。 */
  handleModalClick(event) {
    const openTrigger = event.target.closest('[data-modal-open]');
    if (openTrigger) {
      this.openModal(openTrigger.dataset.modalOpen, openTrigger);
      return;
    }
    const closeTrigger = event.target.closest('[data-modal-close], [data-modal-backdrop]');
    if (closeTrigger) this.closeModal(closeTrigger.closest('[data-shr-modal]'));
  }

  /** 打开弹窗并保存触发元素，便于关闭后恢复焦点。 */
  openModal(name, trigger) {
    const modal = document.querySelector(`[data-shr-modal="${name}"]`);
    if (!modal) return;
    this.modalTrigger = trigger;
    this.activeModal = modal;
    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    document.body.classList.add('shr-modal-open');
    requestAnimationFrame(() => modal.querySelector('[data-modal-close]')?.focus());
  }

  closeModal(modal) {
    if (!modal) return;
    modal.dataset.open = 'false';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    document.body.classList.remove('shr-modal-open');
    this.activeModal = null;
    this.modalTrigger?.focus();
    this.modalTrigger = null;
  }

  /** Esc 关闭当前打开的弹窗。 */
  handleModalKeydown(event) {
    if (event.key === 'Escape' && this.activeModal) this.closeModal(this.activeModal);
  }

  handleSectionLoad(event) {
    this.init(event.target);
  }
}

if (!window.SatellaiHomeRedesign) {
  window.SatellaiHomeRedesign = new SatellaiHomeRedesign();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => window.SatellaiHomeRedesign.init());
  } else {
    requestAnimationFrame(() => window.SatellaiHomeRedesign.init());
  }
}
